import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  ExternalLink, Code2, Upload, Plus, Globe2, Download, Copy,
  CheckCircle2, AlertTriangle, Loader2, Eye, EyeOff, Video, Image as ImageIcon,
  Users, Layers3,
} from "lucide-react";

/**
 * OverviewTab — Phase 3 new tab.
 *
 * One-glance project summary: publish status, KPI cards (scenes / panoramas
 * / units / leads), media-processing health, and a row of high-value
 * quick actions (open public viewer, copy iframe, jump to media upload,
 * add a scene, prepare showroom).
 *
 * The component is purposely read-light: each "action" is a deep link to
 * the right existing tab, not a duplicate form. Keeps the codebase DRY.
 */
export default function OverviewTab({ project, scenes, groups, entities, leads, pid, onJumpTab }) {
  const [mediaCounts, setMediaCounts] = useState(null);
  const [iframeCopied, setIframeCopied] = useState(false);

  useEffect(() => {
    api.get(`/admin/projects/${pid}/media`)
      .then((r) => setMediaCounts(r.data?.counts || {}))
      .catch(() => {});
  }, [pid]);

  const panoramaCount = scenes.filter((s) => s.type === "panorama_360").length;
  const regularSceneCount = scenes.length - panoramaCount;
  const unitsByStatus = entities.reduce((acc, e) => {
    acc[e.status || "available"] = (acc[e.status || "available"] || 0) + 1;
    return acc;
  }, {});
  const etapCount = groups.filter((g) => g.group_type === "etap").length;
  const blockCount = groups.filter((g) => g.group_type === "block").length;
  const newLeads = leads.filter((l) => {
    const d = new Date(l.created_at || 0);
    return d > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  }).length;

  const publicUrl = `/p/${project.slug}`;
  const absoluteUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + publicUrl;
  const iframeCode = `<iframe src="${absoluteUrl}" width="100%" height="720" frameborder="0" allowfullscreen></iframe>`;

  const copyIframe = () => {
    navigator.clipboard.writeText(iframeCode).then(
      () => { setIframeCopied(true); setTimeout(() => setIframeCopied(false), 1800); toast.success("Iframe copied"); },
      () => toast.error("Copy failed"),
    );
  };

  const processingPending = (mediaCounts?.pending || 0) + (mediaCounts?.processing || 0);
  const processingFailed = mediaCounts?.failed || 0;

  return (
    <div className="space-y-6" data-testid="project-overview-tab">
      {/* Header strip */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-light tracking-tight text-zinc-900 truncate">{project.name}</h2>
            <PublishPill published={project.is_published} />
          </div>
          {project.description && <p className="text-sm text-zinc-500 mt-1 max-w-2xl">{project.description}</p>}
          <div className="text-xs text-zinc-500 mt-2 flex items-center gap-3">
            <span className="font-mono">/{project.slug}</span>
            {project.default_language && <span>· Default {project.default_language.toUpperCase()}</span>}
            {(project.supported_languages || []).length > 1 && (
              <span>· {project.supported_languages.length} languages</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm bg-zinc-900 text-white hover:bg-zinc-800"
            data-testid="overview-open-public"
          >
            <ExternalLink className="h-4 w-4" /> Open Public Viewer
          </a>
          <button
            onClick={copyIframe}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm border border-zinc-300 hover:bg-zinc-50"
            data-testid="overview-copy-iframe"
          >
            {iframeCopied ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Code2 className="h-4 w-4" />}
            {iframeCopied ? "Copied" : "Copy iframe"}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="overview-kpis">
        <Kpi icon={Video} label="Scenes" value={regularSceneCount} testid="overview-kpi-scenes" onClick={() => onJumpTab("Scenes")} />
        <Kpi icon={Globe2} label="Panoramas" value={panoramaCount} testid="overview-kpi-panoramas" onClick={() => onJumpTab("Panoramas")} accent={panoramaCount > 0 ? "gold" : undefined} />
        <Kpi icon={Layers3} label="Etaps / Blocks" value={`${etapCount} / ${blockCount}`} testid="overview-kpi-hierarchy" onClick={() => onJumpTab("Etaps & Blocks")} />
        <Kpi icon={ImageIcon} label="Units" value={entities.length} testid="overview-kpi-units" onClick={() => onJumpTab("Units")} subline={unitsByStatus.available ? `${unitsByStatus.available} available` : null} />
        <Kpi icon={Users} label="Leads (7d)" value={newLeads} testid="overview-kpi-leads" onClick={() => onJumpTab("Leads")} subline={`${leads.length} total`} />
        <Kpi icon={Upload} label="Media" value={mediaCounts ? Object.values(mediaCounts).reduce((a, b) => a + (b || 0), 0) : "—"} testid="overview-kpi-media" onClick={() => onJumpTab("Media")} subline={processingPending ? `${processingPending} processing` : (processingFailed ? `${processingFailed} failed` : null)} accent={processingFailed ? "red" : (processingPending ? "amber" : undefined)} />
      </div>

      {/* Warnings */}
      {(processingFailed > 0 || (panoramaCount === 0 && project.is_published) || entities.length === 0) && (
        <div className="space-y-2" data-testid="overview-warnings">
          {processingFailed > 0 && (
            <WarningCard
              icon={AlertTriangle}
              tone="red"
              testid="overview-warn-failed-media"
              title={`${processingFailed} media file${processingFailed > 1 ? "s" : ""} failed to process`}
              cta="Open Media"
              onCta={() => onJumpTab("Media")}
            />
          )}
          {processingPending > 0 && (
            <WarningCard
              icon={Loader2}
              tone="amber"
              spin
              testid="overview-warn-processing"
              title={`${processingPending} upload${processingPending > 1 ? "s" : ""} still optimising`}
              cta="Watch progress"
              onCta={() => onJumpTab("Media")}
            />
          )}
          {entities.length === 0 && (
            <WarningCard
              icon={AlertTriangle}
              tone="amber"
              testid="overview-warn-empty-units"
              title="No units yet — buyers can't shortlist or compare anything."
              cta="Add units"
              onCta={() => onJumpTab("Units")}
            />
          )}
        </div>
      )}

      {/* Quick actions */}
      <div>
        <h3 className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2.5">Quick actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="overview-quick-actions">
          <QuickAction icon={Upload} label="Upload media" onClick={() => onJumpTab("Media")} testid="qa-upload-media" />
          <QuickAction icon={Plus} label="Add scene" onClick={() => onJumpTab("Scenes")} testid="qa-add-scene" />
          <QuickAction icon={Globe2} label="Add panorama" onClick={() => onJumpTab("Panoramas")} testid="qa-add-panorama" />
          <QuickAction icon={Download} label="Import / export Excel" onClick={() => onJumpTab("Units")} testid="qa-import-excel" />
          <QuickAction icon={Eye} label="Configure presentation" onClick={() => onJumpTab("Presentation")} testid="qa-presentation" />
        </div>
      </div>

      {/* Iframe snippet card (for embedding in agent portals) */}
      <div className="rounded-lg border border-zinc-200 p-3 bg-zinc-50" data-testid="overview-embed-snippet">
        <div className="flex items-center gap-2 mb-2">
          <Code2 className="h-3.5 w-3.5 text-zinc-500" />
          <span className="text-xs text-zinc-500 uppercase tracking-wider">Embed snippet</span>
        </div>
        <pre className="text-[11px] font-mono text-zinc-700 whitespace-pre-wrap break-all">{iframeCode}</pre>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, subline, testid, onClick, accent }) {
  const accentCls = accent === "gold" ? "ring-1 ring-[#C4A47C]/30 bg-[#C4A47C]/[0.04]" :
                    accent === "amber" ? "ring-1 ring-amber-200 bg-amber-50/40" :
                    accent === "red" ? "ring-1 ring-rose-200 bg-rose-50/40" : "bg-white";
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border border-zinc-200 px-4 py-3 text-left hover:border-zinc-400 hover:shadow-sm transition ${accentCls}`}
      data-testid={testid}
    >
      <Icon className="h-4 w-4 text-zinc-500 mb-1.5" />
      <div className="text-2xl font-light text-zinc-900 tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mt-0.5">{label}</div>
      {subline && <div className="text-[11px] text-zinc-500 mt-0.5">{subline}</div>}
    </button>
  );
}

function QuickAction({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="rounded-lg border border-zinc-200 px-3 py-3 text-left hover:border-zinc-900 hover:shadow-sm transition flex items-center gap-2.5 text-sm"
      data-testid={testid}
    >
      <Icon className="h-4 w-4 text-zinc-500" />
      <span className="text-zinc-700 truncate">{label}</span>
    </button>
  );
}

function WarningCard({ icon: Icon, tone = "amber", spin, title, cta, onCta, testid }) {
  const toneCls = tone === "red" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-amber-200 bg-amber-50 text-amber-900";
  return (
    <div className={`rounded-lg border px-3 py-2.5 flex items-center gap-3 ${toneCls}`} data-testid={testid}>
      <Icon className={`h-4 w-4 shrink-0 ${spin ? "animate-spin" : ""}`} />
      <div className="text-sm flex-1">{title}</div>
      {cta && (
        <button onClick={onCta} className="text-xs underline hover:no-underline">{cta}</button>
      )}
    </div>
  );
}

function PublishPill({ published }) {
  return published ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs" data-testid="overview-publish-status">
      <Eye className="h-3 w-3" /> Published
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-600 text-xs" data-testid="overview-publish-status">
      <EyeOff className="h-3 w-3" /> Draft
    </span>
  );
}
